import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="logo-fetcher"
export default class extends Controller {
  static targets = ["input", "preview", "previewImage", "hint", "divider", "fetchOption", "uploadOption", "fetchButton"]
  static values = {
    url: String
  }

  connect() {
    this.logoSource = null  // 'fetch' or 'upload'
    this.hidePreview()
    this.listenForFileUpload()
  }

  listenForFileUpload() {
    const fileInput = this.element.querySelector('input[type="file"]')
    if (fileInput) {
      fileInput.addEventListener('change', (event) => {
        const file = event.target.files[0]
        if (file) {
          this.clearFetchData()
          this.logoSource = 'upload'
          this.showUploadedFilePreview(file)
        }
      })
    }
  }

  showUploadedFilePreview(file) {
    const reader = new FileReader()
    reader.onload = (e) => {
      this.showPreview(e.target.result)
    }
    reader.readAsDataURL(file)
  }

  async fetchLogo(event) {
    event.preventDefault()

    const websiteUrl = this.inputTarget.value.trim()

    if (!websiteUrl) {
      this.showHint("Please enter a website URL", "error")
      return
    }

    this.setFetchButtonLoading(true)

    try {
      const response = await window.fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ url: websiteUrl })
      })

      const data = await response.json()

      if (data.success) {
        this.clearUploadData()
        this.logoSource = 'fetch'
        this.createLogoUrlInput(data.logo_url)
        this.showPreview(data.logo_url)
      } else {
        this.setFetchButtonLoading(false)
        this.showHint(data.error || "Could not find logo", "error")
      }
    } catch (error) {
      console.error('Logo fetch error:', error)
      this.setFetchButtonLoading(false)
      this.showHint("Failed to fetch logo. Try again or upload manually.", "error")
    }
  }

  setFetchButtonLoading(loading) {
    if (!this.hasFetchButtonTarget) return

    const label = this.fetchButtonTarget.querySelector('[data-btn-label]') || this.fetchButtonTarget
    if (loading) {
      this.fetchButtonTarget.disabled = true
      this.fetchButtonTarget.dataset.originalLabel = label.textContent
      label.textContent = 'Fetching...'
    } else {
      this.fetchButtonTarget.disabled = false
      if (this.fetchButtonTarget.dataset.originalLabel) {
        label.textContent = this.fetchButtonTarget.dataset.originalLabel
      }
    }
  }

  createLogoUrlInput(logoUrl) {
    let logoUrlInput = this.element.querySelector('input[name="planroom[logo_url]"]')
    if (!logoUrlInput) {
      logoUrlInput = document.createElement('input')
      logoUrlInput.type = 'hidden'
      logoUrlInput.name = 'planroom[logo_url]'
      this.element.appendChild(logoUrlInput)
    }
    logoUrlInput.value = logoUrl
  }

  showPreview(logoUrl) {
    // Hide ALL input UI
    if (this.hasFetchOptionTarget) {
      this.fetchOptionTarget.classList.add('hidden')
    }
    if (this.hasDividerTarget) {
      this.dividerTarget.classList.add('hidden')
    }
    if (this.hasUploadOptionTarget) {
      this.uploadOptionTarget.classList.add('hidden')
    }

    // Show preview
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.remove('hidden')
    }
    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.src = logoUrl
    }
  }

  hidePreview() {
    // Hide preview
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add('hidden')
    }
    // Show ALL input UI
    if (this.hasFetchOptionTarget) {
      this.fetchOptionTarget.classList.remove('hidden')
    }
    if (this.hasDividerTarget) {
      this.dividerTarget.classList.remove('hidden')
    }
    if (this.hasUploadOptionTarget) {
      this.uploadOptionTarget.classList.remove('hidden')
    }
  }

  changeLogo(event) {
    event.preventDefault()
    // Clear all data and return to initial state
    this.clearFetchData()
    this.clearUploadData()
    this.logoSource = null
    this.hidePreview()
    this.resetHint()
    this.setFetchButtonLoading(false)
  }

  triggerUpload(event) {
    event.preventDefault()
    const fileInput = this.element.querySelector('input[type="file"]')
    if (fileInput) {
      fileInput.click()
    }
  }

  clearFetchData() {
    const logoUrlInput = this.element.querySelector('input[name="planroom[logo_url]"]')
    if (logoUrlInput) {
      logoUrlInput.remove()
    }
  }

  clearUploadData() {
    const fileInput = this.element.querySelector('input[type="file"]')
    if (fileInput) {
      fileInput.value = ''
    }
  }

  showHint(message, type = 'default') {
    if (!this.hasHintTarget) return

    // Store original hint text on first call
    if (!this.originalHintText) {
      this.originalHintText = this.hintTarget.textContent
    }

    this.hintTarget.textContent = message
    this.hintTarget.classList.remove('text-red-600')

    if (type === 'error') {
      this.hintTarget.classList.add('text-red-600')
    }
  }

  resetHint() {
    if (!this.hasHintTarget) return

    if (this.originalHintText) {
      this.hintTarget.textContent = this.originalHintText
    }
    this.hintTarget.classList.remove('text-red-600')
  }
}
